// ArtifactLoaderService — loads supplementary Moment artifacts alongside scenarios.
//
// These artifacts are optional — the simulation works without them.
// When available, they enrich the UI with event lineage, impact analysis,
// saga state machines, and field deprecation indicators.
//
// Artifact filenames are read from the manifest's `artifacts` field.

import type { EventCatalog, ImpactAnalysis } from '../types/moment-artifacts.ts';
import type { ManifestArtifacts } from '../types/index.ts';
import type { SagaDefinition } from '../store/sagas/sagaBehavioralEvaluator.ts';
import {
  parsePolicyDefinitions,
  type PolicyDefinition,
} from '../store/sagas/policyChainEvaluator.ts';

/** Default filename for the policy definitions artifact (sibling of saga-state-machines.json). */
export const POLICY_DEFINITIONS_DEFAULT_FILE = 'policy-definitions.json';

// ---------------------------------------------------------------------------
// In-memory cache
// ---------------------------------------------------------------------------

let cachedEventCatalog: EventCatalog | null = null;
let cachedImpactAnalysis: ImpactAnalysis | null = null;
let cachedSagaDefinitions: SagaDefinition[] | null = null;
let cachedPolicyDefinitions: PolicyDefinition[] | null = null;

/**
 * Identity of the source the cache was populated from (e.g. "remote:<baseUrl>"
 * or "user-import"). Prevents one source's artifacts (TicketWave saga
 * definitions, event catalog, impact analysis) from silently feeding
 * assertions and enrichment on another source's scenarios.
 */
let cachedSourceKey: string | null = null;

// ---------------------------------------------------------------------------
// Fetch helpers — all return null on failure (artifacts are optional)
// ---------------------------------------------------------------------------

function freshUrl(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}t=${Date.now()}`;
}

async function fetchOptionalJSON<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(freshUrl(url), { cache: 'no-cache' });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Load all available artifacts from a base URL using manifest artifact references.
 * Returns whatever is available — missing artifacts return null.
 */
export async function fetchArtifacts(
  baseUrl: string,
  artifacts: ManifestArtifacts | undefined,
): Promise<{
  eventCatalog: EventCatalog | null;
  impactAnalysis: ImpactAnalysis | null;
  sagaDefinitions: SagaDefinition[] | null;
  policyDefinitions: PolicyDefinition[] | null;
}> {
  // Clear stale cache so artifacts always reflect the current manifest/session
  clearArtifactCache();
  cachedSourceKey = `remote:${baseUrl}`;

  // Runtime shape check — manifest.artifacts comes from unvalidated remote JSON
  if (!artifacts || typeof artifacts !== 'object') {
    return {
      eventCatalog: null,
      impactAnalysis: null,
      sagaDefinitions: null,
      policyDefinitions: null,
    };
  }

  const [eventCatalog, impactAnalysis, sagaDefinitions, rawPolicyDefinitions] = await Promise.all([
    artifacts.eventCatalog
      ? fetchOptionalJSON<EventCatalog>(`${baseUrl}/${artifacts.eventCatalog}`)
      : Promise.resolve(null),
    artifacts.impactAnalysis
      ? fetchOptionalJSON<ImpactAnalysis>(`${baseUrl}/${artifacts.impactAnalysis}`)
      : Promise.resolve(null),
    artifacts.sagaStateMachines
      ? fetchOptionalJSON<SagaDefinition[]>(`${baseUrl}/${artifacts.sagaStateMachines}`)
      : Promise.resolve(null),
    // policy-definitions.json: by manifest reference when declared, else by
    // its default name (older manifests predate the artifact — a 404 simply
    // yields null, which downstream surfaces as an explicit tier skip).
    fetchOptionalJSON<unknown>(
      `${baseUrl}/${artifacts.policyDefinitions ?? POLICY_DEFINITIONS_DEFAULT_FILE}`,
    ),
  ]);

  // Feature-detect/normalize the policy artifact — malformed content is
  // treated exactly like an absent artifact (never a heuristic pass).
  const policyDefinitions =
    rawPolicyDefinitions !== null ? parsePolicyDefinitions(rawPolicyDefinitions) : null;

  // Cache results
  if (eventCatalog) cachedEventCatalog = eventCatalog;
  if (impactAnalysis) cachedImpactAnalysis = impactAnalysis;
  if (sagaDefinitions) cachedSagaDefinitions = sagaDefinitions;
  if (policyDefinitions) cachedPolicyDefinitions = policyDefinitions;

  return { eventCatalog, impactAnalysis, sagaDefinitions, policyDefinitions };
}

/** Clear all cached artifacts. */
export function clearArtifactCache(): void {
  cachedEventCatalog = null;
  cachedImpactAnalysis = null;
  cachedSagaDefinitions = null;
  cachedPolicyDefinitions = null;
  cachedSourceKey = null;
}

/**
 * Load artifacts from user-provided data (from file upload).
 *
 * REPLACES the cache rather than merging into it: a user import must never
 * inherit another source's artifacts (e.g. remote TicketWave saga definitions
 * feeding assertions on a user's scenarios). Artifacts the upload does not
 * include are simply absent — they are optional.
 */
export function loadLocalArtifacts(
  artifacts: {
    eventCatalog?: EventCatalog;
    impactAnalysis?: ImpactAnalysis;
    sagaDefinitions?: SagaDefinition[];
    /**
     * Accepts either already-normalized definitions or the raw
     * policy-definitions.json artifact content (contract shape:
     * `{name, contextId, trigger, actionDescription?, chainsTo[]}`) —
     * normalized here so the evaluator never sees the contract's `name`
     * field where it expects `policyName`. Malformed content is treated
     * exactly like an absent artifact (explicit tier skip downstream).
     */
    policyDefinitions?: PolicyDefinition[] | unknown;
  },
  sourceKey = 'user-import',
): void {
  clearArtifactCache();
  cachedSourceKey = sourceKey;
  if (artifacts.eventCatalog) cachedEventCatalog = artifacts.eventCatalog;
  if (artifacts.impactAnalysis) cachedImpactAnalysis = artifacts.impactAnalysis;
  if (artifacts.sagaDefinitions) cachedSagaDefinitions = artifacts.sagaDefinitions;
  if (artifacts.policyDefinitions) {
    cachedPolicyDefinitions = parsePolicyDefinitions(artifacts.policyDefinitions);
  }
}

/** Identity of the source that populated the artifact cache (null when empty). */
export function getArtifactSourceKey(): string | null {
  return cachedSourceKey;
}

export function getCachedEventCatalog(): EventCatalog | null {
  return cachedEventCatalog;
}

export function getCachedImpactAnalysis(): ImpactAnalysis | null {
  return cachedImpactAnalysis;
}

/**
 * The websocket source ships no artifacts yet (`moment serve` sends none and
 * the WS path seeds no cache), so a live-bridge run must never consume another
 * source's cached definitions — that evaluates the wrong project's sagas and
 * policies against the trace. Absent a ws-scoped cache the assertion tiers
 * degrade to their explicit defs-missing skips.
 */
function cacheServesSource(activeSource?: 'example' | 'user' | 'websocket'): boolean {
  if (activeSource !== 'websocket') return true;
  return cachedSourceKey !== null && cachedSourceKey.startsWith('ws:');
}

export function getCachedSagaDefinitions(
  activeSource?: 'example' | 'user' | 'websocket',
): SagaDefinition[] | null {
  if (!cacheServesSource(activeSource)) return null;
  return cachedSagaDefinitions;
}

/**
 * Cached policy chain definitions. `null` means the artifact is not available
 * for the current source (old artifact set / missing / malformed) — consumers
 * must emit an explicit tier skip. An empty array means the artifact
 * explicitly declares zero policies.
 */
export function getCachedPolicyDefinitions(
  activeSource?: 'example' | 'user' | 'websocket',
): PolicyDefinition[] | null {
  if (!cacheServesSource(activeSource)) return null;
  return cachedPolicyDefinitions;
}
